import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { 
  BuildingStorefrontIcon, 
  MapPinIcon, 
  EnvelopeIcon, 
  PhoneIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  FunnelIcon,
  EyeIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from "@heroicons/react/24/outline";
import LoadingPage from "../../components/Loader";

import { FaUpload, FaCheckCircle, FaUniversity, FaExchangeAlt } from "react-icons/fa";
import { showSuccessNotification, showErrorNotification, showInfoNotification, showWarningNotification } from '../../components/NotificationManager';


const Restaurants = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editedRestaurant, setEditedRestaurant] = useState({
    name: "",
    email: "",
    description: "",
    location: "",
    contactNumber: "",
    isActive: true,
    university: "",
    bankName: "",
    accountNumber: "",
    accountHolder: "",
    averageRating: 0,
    totalRatings: 0
  });
  const [universities, setUniversities] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    withMenu: 0
  });
  
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newRestaurant, setNewRestaurant] = useState({
    name: "",
    email: "",
    university: "",
    location: "",
    description: "",
    contactNumber: "",
    imageUrl: "",
    bankName: "",
    accountNumber: "",
    accountHolder: ""
  });
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [detailsRestaurant, setDetailsRestaurant] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedCreateImage, setSelectedCreateImage] = useState(null);
  const [selectedEditImage, setSelectedEditImage] = useState(null);
  
  // New state for advanced filtering and pagination
  const [filters, setFilters] = useState({
    university: "",
    isActive: "",
    sortBy: "name",
    sortOrder: "asc"
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isUniversityChangeModalOpen, setIsUniversityChangeModalOpen] = useState(false);
  const [bulkSelectedRestaurants, setBulkSelectedRestaurants] = useState([]);
  const [bulkTargetUniversity, setBulkTargetUniversity] = useState("");
  const [isBulkUpdateLoading, setIsBulkUpdateLoading] = useState(false);

  useEffect(() => {
    fetchRestaurants();
    fetchUniversities();
  }, [filters, pagination.currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRestaurants = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // Build query parameters according to new API documentation
      const queryParams = new URLSearchParams({
        page: pagination.currentPage.toString(),
        limit: pagination.itemsPerPage.toString(),
        includeOrders: 'true' // Request orders data to be included
      });
      
      if (filters.university) queryParams.append("university", filters.university);
      if (filters.isActive !== "") queryParams.append("isActive", filters.isActive);
      
      const response = await axios.get(
        `https://mongobyte-hc73.onrender.com/api/v1/superadmin/restaurants?${queryParams.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000 // 30 second timeout
        }
      );
      
      // console.log("Restaurant API Response:", response.data);
      
      // Handle new API response format
      if (response.data.restaurants) {
        const restaurantsWithOrders = await Promise.all(
          response.data.restaurants.map(async (restaurant) => {
            // Ensure orders is always an array
            if (!restaurant.orders || !Array.isArray(restaurant.orders)) {
              // console.log(`Restaurant ${restaurant.name} has no orders or invalid orders data, attempting to fetch separately...`);
              try {
                // Try to fetch orders for this restaurant specifically
                const ordersResponse = await axios.get(
                  `https://mongobyte-hc73.onrender.com/api/v1/superadmin/restaurant/${restaurant._id}/orders`,
                  {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 15000
                  }
                );
                restaurant.orders = Array.isArray(ordersResponse.data) ? ordersResponse.data : 
                                  Array.isArray(ordersResponse.data.orders) ? ordersResponse.data.orders : [];
                // console.log(`Fetched ${restaurant.orders.length} orders for ${restaurant.name}`);
              } catch (orderError) {
                // console.log(`Could not fetch orders for ${restaurant.name}:`, orderError.message);
                restaurant.orders = [];
              }
            }
            return restaurant;
          })
        );
        
        setRestaurants(restaurantsWithOrders);
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            ...response.data.pagination
          }));
        }
      } else {
        // Fallback for old API format
        const restaurantsWithOrders = await Promise.all(
          (response.data || []).map(async (restaurant) => {
            if (!restaurant.orders || !Array.isArray(restaurant.orders)) {
              try {
                const ordersResponse = await axios.get(
                  `https://mongobyte-hc73.onrender.com/api/v1/superadmin/restaurant/${restaurant._id}/orders`,
                  {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 15000
                  }
                );
                restaurant.orders = Array.isArray(ordersResponse.data) ? ordersResponse.data : 
                                  Array.isArray(ordersResponse.data.orders) ? ordersResponse.data.orders : [];
              } catch (orderError) {
                restaurant.orders = [];
              }
            }
            return restaurant;
          })
        );
        setRestaurants(restaurantsWithOrders);
      }
      
      // Calculate stats
      const allRestaurants = response.data.restaurants || response.data;
      const stats = {
        total: allRestaurants.length,
        active: allRestaurants.filter(r => r.isActive).length,
        inactive: allRestaurants.filter(r => !r.isActive).length,
        withMenu: allRestaurants.filter(r => Array.isArray(r.meals) && r.meals.length > 0).length
      };
      
      setStats(stats);
    } catch (error) {
      console.error("Error fetching restaurants:", error);
      showErrorNotification("Failed to load restaurants");
    } finally {
      setLoading(false);
    }
  };

  const fetchUniversities = async () => {
    try {
      const response = await axios.get("https://mongobyte-hc73.onrender.com/api/v1/universities");
      setUniversities(response.data.data);
    } catch (error) {
      console.error("Error fetching universities:", error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleEditRestaurant = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setEditedRestaurant({
      name: restaurant.name,
      email: restaurant.email,
      description: restaurant.description || "",
      location: restaurant.location || "",
      contactNumber: restaurant.contactNumber || "",
      isActive: restaurant.isActive,
      university: restaurant.university || "",
      bankName: restaurant.bankName || "",
      accountNumber: restaurant.accountNumber || "",
      accountHolder: restaurant.accountHolder || "",
      averageRating: restaurant.averageRating || 0,
      totalRatings: restaurant.totalRatings || 0
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteRestaurant = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setIsDeleteModalOpen(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditedRestaurant({
      ...editedRestaurant,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleEditImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedEditImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitEdit = async () => {
    try {
      const token = localStorage.getItem("token");
      let imageUrl = editedRestaurant.imageUrl;
      if (selectedEditImage) {
        imageUrl = await uploadImage(selectedEditImage);
      }
      
      // Use new super admin API endpoint for silent updates
      const response = await axios.put(
        `https://mongobyte-hc73.onrender.com/api/v1/superadmin/restaurants/${selectedRestaurant._id}`,
        { ...editedRestaurant, imageUrl },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        }
      );
      
      // Update restaurants list with response data
      if (response.data.restaurant) {
        setRestaurants(restaurants.map(restaurant => 
          restaurant._id === selectedRestaurant._id 
            ? { ...restaurant, ...response.data.restaurant } 
            : restaurant
        ));
      } else {
        setRestaurants(restaurants.map(restaurant => 
          restaurant._id === selectedRestaurant._id 
            ? { ...restaurant, ...editedRestaurant, imageUrl } 
            : restaurant
        ));
      }
      
      if (selectedRestaurant.isActive !== editedRestaurant.isActive) {
        setStats({
          ...stats,
          active: editedRestaurant.isActive 
            ? stats.active + 1 
            : stats.active - 1,
          inactive: editedRestaurant.isActive 
            ? stats.inactive - 1 
            : stats.inactive + 1
        });
      }
      setIsEditModalOpen(false);
      setSelectedEditImage(null);
      showSuccessNotification("Restaurant updated by super admin successfully! (No notification sent to restaurant)");
    } catch (error) {
      console.error("Error updating restaurant:", error);
      showErrorNotification("Failed to update restaurant");
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // Try to delete using the restaurant's _id first, then customId as fallback
      const restaurantId = selectedRestaurant.customId || selectedRestaurant._id;
      
      console.log('Attempting to delete restaurant:', {
        _id: selectedRestaurant._id,
        customId: selectedRestaurant.customId,
        name: selectedRestaurant.name,
        using: restaurantId
      });
      
      // Use new super admin API endpoint for deletion
      const response = await axios.delete(
        `https://mongobyte-hc73.onrender.com/api/v1/superadmin/restaurants/${restaurantId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      console.log('Delete response:', response.data);
      
      // Update the restaurants list
      setRestaurants(restaurants.filter(restaurant => restaurant._id !== selectedRestaurant._id));
      
      // Update stats
      setStats({
        ...stats,
        total: stats.total - 1,
        active: selectedRestaurant.isActive ? stats.active - 1 : stats.active,
        inactive: selectedRestaurant.isActive ? stats.inactive : stats.inactive - 1,
        withMenu: (selectedRestaurant.menu && selectedRestaurant.menu.length > 0) 
          ? stats.withMenu - 1 
          : stats.withMenu
      });
      
      setIsDeleteModalOpen(false);
      showSuccessNotification(response.data.message || "Restaurant deleted successfully!");
    } catch (error) {
      console.error("Error deleting restaurant:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to delete restaurant";
      showErrorNotification(`Error deleting restaurant: ${errorMessage}`);
    }
  };

  const handleCreateInputChange = (e) => {
    const { name, value } = e.target;
    setNewRestaurant({
      ...newRestaurant,
      [name]: value
    });
  };

  const handleCreateImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedCreateImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateRestaurant = async () => {
    try {
      const token = localStorage.getItem("token");
      let imageUrl = newRestaurant.imageUrl;
      if (selectedCreateImage) {
        imageUrl = await uploadImage(selectedCreateImage);
      }
      
      // Use new super admin API endpoint for creation
      await axios.post(
        "https://mongobyte-hc73.onrender.com/api/v1/superadmin/restaurants",
        { ...newRestaurant, imageUrl },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      showSuccessNotification("Restaurant created successfully!");
      setIsCreateModalOpen(false);
      setNewRestaurant({
        name: "",
        email: "",
        university: "",
        location: "",
        description: "",
        contactNumber: "",
        imageUrl: "",
        bankName: "",
        accountNumber: "",
        accountHolder: ""
      });
      setSelectedCreateImage(null);
      fetchRestaurants();
    } catch (error) {
      console.error("Error creating restaurant:", error);
      const errorMessage = error.response?.data?.message || "Failed to create restaurant";
      showErrorNotification(errorMessage);
    }
  };

  const handleToggleActive = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `https://mongobyte-hc73.onrender.com/api/v1/restaurants/${id}/toggle-active`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      showSuccessNotification("Status updated");
      fetchRestaurants();
    } catch (error) {
      showErrorNotification("Failed to update status");
    }
  };

  const handleViewDetails = async (id) => {
    setDetailsLoading(true);
    setIsDetailsModalOpen(true);
    try {
      const token = localStorage.getItem("token");
      
      // Use new super admin API endpoint for getting restaurant details
      const response = await axios.get(
        `https://mongobyte-hc73.onrender.com/api/v1/superadmin/restaurants/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setDetailsRestaurant(response.data);
    } catch (error) {
      console.error("Error fetching restaurant details:", error);
      showErrorNotification("Failed to fetch details");
      setDetailsRestaurant(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  // New filter handlers
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page when filtering
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  // eslint-disable-next-line no-unused-vars
  const handleSortChange = (sortBy) => {
    const newSortOrder = filters.sortBy === sortBy && filters.sortOrder === "asc" ? "desc" : "asc";
    setFilters(prev => ({
      ...prev,
      sortBy,
      sortOrder: newSortOrder
    }));
  };

  // Bulk operations for university assignment
  const handleBulkSelectRestaurant = (restaurantId) => {
    setBulkSelectedRestaurants(prev => {
      if (prev.includes(restaurantId)) {
        return prev.filter(id => id !== restaurantId);
      } else {
        return [...prev, restaurantId];
      }
    });
  };

  const handleSelectAllRestaurants = () => {
    if (bulkSelectedRestaurants.length === filteredRestaurants.length) {
      setBulkSelectedRestaurants([]);
    } else {
      setBulkSelectedRestaurants(filteredRestaurants.map(r => r._id));
    }
  };

  const handleBulkUniversityChange = async () => {
    if (bulkSelectedRestaurants.length === 0 || !bulkTargetUniversity) {
      showErrorNotification("Please select restaurants and target university");
      return;
    }

    setIsBulkUpdateLoading(true);
    try {
      const token = localStorage.getItem("token");
      let successCount = 0;
      let errorCount = 0;

      // Process each restaurant individually for better error handling
      for (const restaurantId of bulkSelectedRestaurants) {
        try {
          await axios.put(
            `https://mongobyte-hc73.onrender.com/api/v1/superadmin/restaurants/${restaurantId}`,
            { university: bulkTargetUniversity },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
            }
          );
          successCount++;
        } catch (error) {
          console.error(`Error updating restaurant ${restaurantId}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        showSuccessNotification(`${successCount} restaurant(s) updated successfully! (No notifications sent to restaurants)`);
        fetchRestaurants(); // Refresh the list
      }
      
      if (errorCount > 0) {
        showErrorNotification(`${errorCount} restaurant(s) failed to update`);
      }

      setBulkSelectedRestaurants([]);
      setBulkTargetUniversity("");
      setIsUniversityChangeModalOpen(false);
    } catch (error) {
      console.error("Bulk update error:", error);
      showErrorNotification("Failed to perform bulk update");
    } finally {
      setIsBulkUpdateLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      university: "",
      isActive: "",
      sortBy: "name",
      sortOrder: "asc"
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const uploadImage = async (imageData) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "https://mongobyte-hc73.onrender.com/api/v1/users/upload",
        { image: imageData },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
        }
      );
      return response.data.url;
    } catch (error) {
      showErrorNotification("Image upload failed");
      throw error;
    }
  };

  const filteredRestaurants = restaurants.filter(restaurant => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      restaurant.name.toLowerCase().includes(searchLower) ||
      restaurant.email.toLowerCase().includes(searchLower) ||
      restaurant.location?.toLowerCase().includes(searchLower) ||
      restaurant.description?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 pt-20 pb-24 md:pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Super Admin Restaurant Management</h1>
            <p className="mt-1 text-sm text-gray-500">
              Complete control over restaurant profiles with silent operations
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
            <button 
              onClick={() => setIsFilterModalOpen(true)}
              className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors shadow-md"
            >
              <FunnelIcon className="w-5 h-5" />
              Filters
            </button>
            
            {bulkSelectedRestaurants.length > 0 && (
              <button 
                onClick={() => setIsUniversityChangeModalOpen(true)}
                className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white py-2 px-4 rounded-lg transition-colors shadow-md"
              >
                <FaExchangeAlt className="w-4 h-4" />
                Bulk Assign ({bulkSelectedRestaurants.length})
              </button>
            )}
            
            <button 
              onClick={fetchRestaurants} 
              className="flex items-center gap-2 bg-cheese hover:bg-yellow-500 text-crust py-2 px-4 rounded-lg transition-colors shadow-md"
            >
              <ArrowPathIcon className="w-5 h-5" />
              Refresh
            </button>
            
            <button 
              onClick={() => setIsCreateModalOpen(true)} 
              className="flex items-center gap-2 bg-pepperoni hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors shadow-md"
            >
              <BuildingStorefrontIcon className="w-5 h-5" />
              Add New
            </button>
          </div>
        </div>

        {/* Enhanced Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white p-4 rounded-xl shadow-md border-l-4 border-cheese"
          >
            <div className="font-medium text-gray-500 text-sm">Total Restaurants</div>
            <div className="text-2xl font-bold text-gray-900">{pagination.totalItems || stats.total}</div>
            <div className="text-xs text-gray-400 mt-1">Across all universities</div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="bg-white p-4 rounded-xl shadow-md border-l-4 border-green-400"
          >
            <div className="font-medium text-gray-500 text-sm">Active</div>
            <div className="text-2xl font-bold text-gray-900">{stats.active}</div>
            <div className="text-xs text-gray-400 mt-1">Currently serving</div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="bg-white p-4 rounded-xl shadow-md border-l-4 border-red-400"
          >
            <div className="font-medium text-gray-500 text-sm">Inactive</div>
            <div className="text-2xl font-bold text-gray-900">{stats.inactive}</div>
            <div className="text-xs text-gray-400 mt-1">Temporarily closed</div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
            className="bg-white p-4 rounded-xl shadow-md border-l-4 border-blue-400"
          >
            <div className="font-medium text-gray-500 text-sm">With Menu Items</div>
            <div className="text-2xl font-bold text-gray-900">{stats.withMenu}</div>
            <div className="text-xs text-gray-400 mt-1">Ready to serve</div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
            className="bg-white p-4 rounded-xl shadow-md border-l-4 border-purple-400"
          >
            <div className="font-medium text-gray-500 text-sm">Universities</div>
            <div className="text-2xl font-bold text-gray-900">{universities.length}</div>
            <div className="text-xs text-gray-400 mt-1">Total campuses</div>
          </motion.div>
        </div>

        {/* Active Filters Display */}
        {(filters.university || filters.isActive !== "" || searchTerm) && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-blue-800">Active Filters:</span>
              
              {filters.university && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  University: {universities.find(u => u._id === filters.university)?.name || "Unknown"}
                  <button 
                    onClick={() => handleFilterChange("university", "")}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              
              {filters.isActive !== "" && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Status: {filters.isActive === "true" ? "Active" : "Inactive"}
                  <button 
                    onClick={() => handleFilterChange("isActive", "")}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              
              {searchTerm && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Search: "{searchTerm}"
                  <button 
                    onClick={() => setSearchTerm("")}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              
              <button 
                onClick={clearFilters}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                Clear All
              </button>
            </div>
          </div>
        )}

        {/* Bulk Selection and Search */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search restaurants by name, email, location..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-cheese focus:border-transparent"
            />
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          
          {filteredRestaurants.length > 0 && (
            <div className="flex gap-2">
              <button
                onClick={handleSelectAllRestaurants}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                <input
                  type="checkbox"
                  checked={bulkSelectedRestaurants.length === filteredRestaurants.length}
                  onChange={() => {}} // Handled by onClick
                  className="rounded"
                />
                Select All ({filteredRestaurants.length})
              </button>
              
              {bulkSelectedRestaurants.length > 0 && (
                <span className="flex items-center px-3 py-2 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium">
                  {bulkSelectedRestaurants.length} selected
                </span>
              )}
            </div>
          )}
        </div>

        {/* Restaurants List */}
        {filteredRestaurants.length === 0 ? (
          <div className="bg-white rounded-xl p-8 shadow-md text-center">
            <BuildingStorefrontIcon className="w-16 h-16 mx-auto text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No restaurants found</h3>
            <p className="mt-1 text-gray-500">
              {searchTerm ? "Try different search terms" : "There are no restaurants registered yet"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-6 lg:gap-8">
            {filteredRestaurants.map((restaurant) => (
              <motion.div
                key={restaurant._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-xl shadow-lg overflow-hidden border-2 transition-all duration-200 hover:shadow-xl ${
                  bulkSelectedRestaurants.includes(restaurant._id) 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex flex-col sm:flex-row">
                  {/* Mobile: Top bar with checkbox and image | Desktop: Left side */}
                  <div className="sm:w-20 bg-gray-50 p-4 sm:p-6 flex sm:flex-col items-center justify-between sm:justify-start">
                    <input
                      type="checkbox"
                      checked={bulkSelectedRestaurants.includes(restaurant._id)}
                      onChange={() => handleBulkSelectRestaurant(restaurant._id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-5 w-5"
                    />
                    <div className="text-xs text-gray-500 sm:mt-4 sm:transform sm:-rotate-90 whitespace-nowrap">
                      {restaurant.customId || `#${restaurant._id.slice(-6)}`}
                    </div>
                  </div>

                  {/* Restaurant Image */}
                  <div className="sm:w-1/3 bg-gradient-to-br from-gray-100 to-gray-200 p-4 sm:p-6 flex items-center justify-center">
                    {restaurant.imageUrl ? (
                      <img 
                        src={restaurant.imageUrl} 
                        alt={restaurant.name} 
                        className="w-full h-32 sm:h-36 object-cover rounded-xl shadow-sm"
                      />
                    ) : (
                      <div className="w-full h-32 sm:h-36 bg-gradient-to-br from-cheese to-pepperoni rounded-xl flex items-center justify-center">
                        <BuildingStorefrontIcon className="w-12 sm:w-16 h-12 sm:h-16 text-white" />
                      </div>
                    )}
                  </div>
                  
                  {/* Restaurant Information */}
                  <div className="flex-1 p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4">
                      <div className="flex-1 mb-4 sm:mb-0">
                        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 flex flex-col sm:flex-row sm:items-center gap-2">
                          <span className="break-words">{restaurant.name}</span>
                          {restaurant.isActive ? (
                            <span className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full border border-green-300 flex items-center gap-1 self-start">
                              <CheckCircleIcon className="w-4 h-4" />
                              Active
                            </span>
                          ) : (
                            <span className="bg-red-100 text-red-800 text-sm px-3 py-1 rounded-full border border-red-300 flex items-center gap-1 self-start">
                              <XCircleIcon className="w-4 h-4" />
                              Inactive
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-500 mb-2">
                          Joined: {format(new Date(restaurant.createdAt), "MMM d, yyyy")}
                        </p>
                        
                        {/* Rating Display */}
                        {restaurant.averageRating > 0 && (
                          <div className="flex items-center gap-1">
                            <span className="text-yellow-500 text-lg">★</span>
                            <span className="text-sm font-medium">{restaurant.averageRating.toFixed(1)}</span>
                            <span className="text-xs text-gray-500">({restaurant.totalRatings} reviews)</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-2">
                        <button 
                          onClick={() => handleViewDetails(restaurant._id)}
                          className="p-2 text-gray-500 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                          title="View Details"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </button>
                        <button 
                          onClick={() => handleEditRestaurant(restaurant)}
                          className="p-2 text-gray-500 hover:text-pepperoni rounded-lg hover:bg-orange-50 transition-colors"
                          title="Edit Restaurant"
                        >
                          <PencilSquareIcon className="w-5 h-5" />
                        </button>
                        <button 
                          onClick={() => handleDeleteRestaurant(restaurant)}
                          className="p-2 text-gray-500 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                          title="Delete Restaurant"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(restaurant._id)}
                          className={`p-2 rounded-lg transition-colors ${restaurant.isActive ? 'text-green-600 hover:bg-green-50' : 'text-red-600 hover:bg-red-50'}`}
                          title={restaurant.isActive ? "Deactivate" : "Activate"}
                        >
                          {restaurant.isActive ? <CheckCircleIcon className="w-5 h-5" /> : <XCircleIcon className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                    
                    {/* Restaurant Details */}
                    <div className="space-y-2 mb-4">
                      <p className="text-sm flex items-center gap-3">
                        <EnvelopeIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-600 break-all">{restaurant.email}</span>
                      </p>
                      <p className="text-sm flex items-center gap-3">
                        <PhoneIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-600 break-words">{restaurant.contactNumber || "No phone number"}</span>
                      </p>
                      <p className="text-sm flex items-start gap-3">
                        <MapPinIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600 break-words leading-relaxed">{restaurant.location || "No location specified"}</span>
                      </p>
                      <p className="text-sm flex items-center gap-3">
                        <FaUniversity className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-600 break-words">
                          {typeof restaurant.university === 'object' && restaurant.university?.name 
                            ? restaurant.university.name
                            : universities.find(u => u._id === restaurant.university)?.name || "No university"}
                        </span>
                      </p>
                      {restaurant.bankName && restaurant.accountNumber && (
                        <p className="text-sm flex items-center gap-3">
                          <BanknotesIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-600">
                            {restaurant.bankName} - ****{restaurant.accountNumber.slice(-4)} ({restaurant.accountHolder})
                          </span>
                        </p>
                      )}
                    </div>
                    
                    {/* Footer Stats */}
                    <div className="mt-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                          {Array.isArray(restaurant.meals) ? restaurant.meals.length : 0} menu items
                        </span>
                      </div>
                      <div className="flex gap-2">
                        {Array.isArray(restaurant.orders) && restaurant.orders.length > 0 ? (
                          <span className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
                            {restaurant.orders.length} orders
                          </span>
                        ) : (
                          <span className="text-sm bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                            No orders yet
                          </span>
                        )}
                        
                        {restaurant.customId && (
                          <span className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                            ID: {restaurant.customId}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          )}
          {pagination.totalPages > 1 && (
            <div className="mt-8 flex justify-center items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.currentPage >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 text-sm rounded-lg ${
                        pageNum === pagination.currentPage
                          ? 'bg-cheese text-crust font-medium'
                          : 'bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              
              <span className="ml-4 text-sm text-gray-500">
                Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} total)
              </span>
            </div>
          )}
      </div>

      {/* Edit Restaurant Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 bg-cheese rounded-t-xl">
              <h3 className="text-xl font-bold text-crust">Edit Restaurant</h3>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo/Image</label>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <img
                      src={selectedEditImage || editedRestaurant.imageUrl || "https://res.cloudinary.com/dol47ucmj/image/upload/v1729928426/jm9dfybhu5pqqevrhyke.jpg"}
                      alt="Preview"
                      className="w-20 h-20 rounded-full object-cover border-2 border-cheese"
                    />
                    {selectedEditImage && (
                      <div className="absolute -top-2 -right-2 bg-pepperoni text-white p-1 rounded-full">
                        <FaCheckCircle className="text-xs" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleEditImageChange}
                      className="hidden"
                      id="restaurant-edit-upload"
                    />
                    <label
                      htmlFor="restaurant-edit-upload"
                      className="cursor-pointer bg-orange-50 hover:bg-orange-100 text-crust font-medium py-2 px-4 rounded-xl border-2 border-dashed border-cheese hover:border-pepperoni transition-all flex items-center justify-center gap-2"
                    >
                      <FaUpload />
                      Choose Logo
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Restaurant Name</label>
                <input
                  type="text"
                  name="name"
                  value={editedRestaurant.name}
                  onChange={handleEditInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={editedRestaurant.email}
                  onChange={handleEditInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={editedRestaurant.description}
                  onChange={handleEditInputChange}
                  rows="3"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                ></textarea>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  name="location"
                  value={editedRestaurant.location}
                  onChange={handleEditInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
                <input
                  type="text"
                  name="contactNumber"
                  value={editedRestaurant.contactNumber}
                  onChange={handleEditInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">University</label>
                <select
                  name="university"
                  value={editedRestaurant.university}
                  onChange={handleEditInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                >
                  <option value="">Select University</option>
                  {universities.map(university => (
                    <option key={university._id} value={university._id}>
                      {university.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                <input
                  type="text"
                  name="bankName"
                  value={editedRestaurant.bankName || ''}
                  onChange={handleEditInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                <input
                  type="text"
                  name="accountNumber"
                  value={editedRestaurant.accountNumber || ''}
                  onChange={handleEditInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder</label>
                <input
                  type="text"
                  name="accountHolder"
                  value={editedRestaurant.accountHolder || ''}
                  onChange={handleEditInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                  required
                />
              </div>
              
              {/* Super Admin Rating Override Section */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-pepperoni" />
                  Super Admin Rating Override
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Average Rating</label>
                    <input
                      type="number"
                      name="averageRating"
                      value={editedRestaurant.averageRating || 0}
                      onChange={handleEditInputChange}
                      min="0"
                      max="5"
                      step="0.1"
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Ratings</label>
                    <input
                      type="number"
                      name="totalRatings"
                      value={editedRestaurant.totalRatings || 0}
                      onChange={handleEditInputChange}
                      min="0"
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Super admin can override restaurant ratings for administrative purposes.
                </p>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={editedRestaurant.isActive}
                  onChange={handleEditInputChange}
                  className="h-4 w-4 text-cheese border-gray-300 rounded focus:ring-cheese"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                  Active Status
                </label>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitEdit}
                  className="px-4 py-2 bg-cheese text-crust rounded-lg shadow-sm hover:bg-yellow-500 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full"
          >
            <div className="p-6 bg-red-500 rounded-t-xl">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <ExclamationTriangleIcon className="w-6 h-6" />
                Confirm Deletion
              </h3>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete the restaurant <span className="font-semibold">{selectedRestaurant?.name}</span>? 
                This action cannot be undone.
              </p>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg shadow-sm hover:bg-red-600 transition-colors"
                >
                  Delete Restaurant
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Create Restaurant Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 bg-cheese rounded-t-xl">
              <h3 className="text-xl font-bold text-crust">Create Restaurant</h3>
            </div>
            <div className="p-6 space-y-4">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Logo/Image</label>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <img
                      src={selectedCreateImage || newRestaurant.imageUrl || "https://res.cloudinary.com/dol47ucmj/image/upload/v1729928426/jm9dfybhu5pqqevrhyke.jpg"}
                      alt="Preview"
                      className="w-20 h-20 rounded-full object-cover border-2 border-cheese"
                    />
                    {selectedCreateImage && (
                      <div className="absolute -top-2 -right-2 bg-pepperoni text-white p-1 rounded-full">
                        <FaCheckCircle className="text-xs" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleCreateImageChange}
                      className="hidden"
                      id="restaurant-create-upload"
                    />
                    <label
                      htmlFor="restaurant-create-upload"
                      className="cursor-pointer bg-orange-50 hover:bg-orange-100 text-crust font-medium py-2 px-4 rounded-xl border-2 border-dashed border-cheese hover:border-pepperoni transition-all flex items-center justify-center gap-2"
                    >
                      <FaUpload />
                      Choose Logo
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Restaurant Name</label>
                <input
                  type="text"
                  name="name"
                  value={newRestaurant.name}
                  onChange={handleCreateInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={newRestaurant.email}
                  onChange={handleCreateInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={newRestaurant.description}
                  onChange={handleCreateInputChange}
                  rows="3"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                ></textarea>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  name="location"
                  value={newRestaurant.location}
                  onChange={handleCreateInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
                <input
                  type="text"
                  name="contactNumber"
                  value={newRestaurant.contactNumber}
                  onChange={handleCreateInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                <input
                  type="text"
                  name="bankName"
                  value={newRestaurant.bankName || ''}
                  onChange={handleCreateInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                <input
                  type="text"
                  name="accountNumber"
                  value={newRestaurant.accountNumber || ''}
                  onChange={handleCreateInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder</label>
                <input
                  type="text"
                  name="accountHolder"
                  value={newRestaurant.accountHolder || ''}
                  onChange={handleCreateInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">University</label>
                <select
                  name="university"
                  value={newRestaurant.university}
                  onChange={handleCreateInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cheese focus:border-transparent"
                >
                  <option value="">Select University</option>
                  {universities.map(university => (
                    <option key={university._id} value={university._id}>
                      {university.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRestaurant}
                  className="px-4 py-2 bg-cheese text-crust rounded-lg shadow-sm hover:bg-yellow-500 transition-colors"
                >
                  Create
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Details Modal */}
      {isDetailsModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 bg-blue-400 rounded-t-xl">
              <h3 className="text-xl font-bold text-white">Restaurant Details</h3>
            </div>
            <div className="p-6 space-y-4">
              {detailsLoading ? (
                <div className="text-center py-8">
                  <ArrowPathIcon className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-2" />
                  <p>Loading restaurant details...</p>
                </div>
              ) : detailsRestaurant ? (
                <div className="space-y-4">
                  {/* Basic Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <BuildingStorefrontIcon className="w-4 h-4" />
                      Basic Information
                    </h4>
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      <div><strong>Name:</strong> {detailsRestaurant.name}</div>
                      <div><strong>Email:</strong> {detailsRestaurant.email}</div>
                      <div><strong>Custom ID:</strong> {detailsRestaurant.customId || `#${detailsRestaurant._id.slice(-6)}`}</div>
                      <div><strong>MongoDB ID:</strong> <code className="bg-gray-200 px-1 rounded text-xs">{detailsRestaurant._id}</code></div>
                      <div><strong>Status:</strong> 
                        <span className={`ml-2 px-2 py-0.5 rounded text-xs ${detailsRestaurant.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {detailsRestaurant.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Location & Contact */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <MapPinIcon className="w-4 h-4" />
                      Location & Contact
                    </h4>
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      <div><strong>Address:</strong> {detailsRestaurant.location || "Not specified"}</div>
                      <div><strong>Phone:</strong> {detailsRestaurant.contactNumber || "Not provided"}</div>
                      <div><strong>University:</strong> 
                        {typeof detailsRestaurant.university === 'object' && detailsRestaurant.university?.name 
                          ? detailsRestaurant.university.name
                          : universities.find(u => u._id === detailsRestaurant.university)?.name || "No university assigned"}
                      </div>
                    </div>
                  </div>

                  {/* Performance Metrics */}
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <CheckCircleIcon className="w-4 h-4" />
                      Performance Metrics
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <strong>Average Rating:</strong>
                        <div className="flex items-center gap-1 mt-1">
                          <span className="text-yellow-500">★</span>
                          <span>{detailsRestaurant.averageRating ? detailsRestaurant.averageRating.toFixed(1) : 'No rating'}</span>
                        </div>
                      </div>
                      <div>
                        <strong>Total Reviews:</strong>
                        <div className="mt-1">{detailsRestaurant.totalRatings || 0}</div>
                      </div>
                      <div>
                        <strong>Menu Items:</strong>
                        <div className="mt-1">{Array.isArray(detailsRestaurant.meals) ? detailsRestaurant.meals.length : 0}</div>
                      </div>
                      <div>
                        <strong>Total Orders:</strong>
                        <div className="mt-1">{Array.isArray(detailsRestaurant.orders) ? detailsRestaurant.orders.length : 0}</div>
                      </div>
                    </div>
                  </div>

                  {/* Financial Information */}
                  {(detailsRestaurant.bankName || detailsRestaurant.accountNumber) && (
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <BanknotesIcon className="w-4 h-4" />
                        Financial Information
                      </h4>
                      <div className="grid grid-cols-1 gap-2 text-sm">
                        <div><strong>Bank Name:</strong> {detailsRestaurant.bankName || "Not provided"}</div>
                        <div><strong>Account Number:</strong> {detailsRestaurant.accountNumber ? `****${detailsRestaurant.accountNumber.slice(-4)}` : "Not provided"}</div>
                        <div><strong>Account Holder:</strong> {detailsRestaurant.accountHolder || "Not provided"}</div>
                      </div>
                    </div>
                  )}

                  {/* System Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-3">System Information</h4>
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      <div><strong>Created:</strong> {detailsRestaurant.createdAt ? format(new Date(detailsRestaurant.createdAt), "PPp") : "Unknown"}</div>
                      <div><strong>Last Updated:</strong> {detailsRestaurant.updatedAt ? format(new Date(detailsRestaurant.updatedAt), "PPp") : "Unknown"}</div>
                      {detailsRestaurant.description && (
                        <div><strong>Description:</strong> {detailsRestaurant.description}</div>
                      )}
                    </div>
                  </div>

                  {/* Restaurant Image */}
                  {detailsRestaurant.imageUrl && (
                    <div className="bg-white p-4 rounded-lg border">
                      <h4 className="font-semibold text-gray-800 mb-3">Restaurant Image</h4>
                      <img 
                        src={detailsRestaurant.imageUrl} 
                        alt="Restaurant" 
                        className="w-full max-w-sm h-40 object-cover rounded-lg mx-auto"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-red-500 py-8">
                  <ExclamationTriangleIcon className="w-8 h-8 mx-auto mb-2" />
                  <p>Restaurant details could not be loaded.</p>
                </div>
              )}
              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setIsDetailsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Advanced Filter Modal */}
      {isFilterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full"
          >
            <div className="p-6 bg-blue-500 rounded-t-xl">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <FunnelIcon className="w-6 h-6" />
                Advanced Filters
              </h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">University</label>
                <select
                  value={filters.university}
                  onChange={(e) => handleFilterChange("university", e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Universities</option>
                  {universities.map(university => (
                    <option key={university._id} value={university._id}>
                      {university.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filters.isActive}
                  onChange={(e) => handleFilterChange("isActive", e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  <option value="true">Active Only</option>
                  <option value="false">Inactive Only</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <div className="flex gap-2">
                  <select
                    value={filters.sortBy}
                    onChange={(e) => handleFilterChange("sortBy", e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="name">Restaurant Name</option>
                    <option value="createdAt">Date Created</option>
                    <option value="averageRating">Rating</option>
                    <option value="totalRatings">Total Reviews</option>
                  </select>
                  <button
                    onClick={() => handleFilterChange("sortOrder", filters.sortOrder === "asc" ? "desc" : "asc")}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    title={`Sort ${filters.sortOrder === "asc" ? "Descending" : "Ascending"}`}
                  >
                    {filters.sortOrder === "asc" ? <ArrowUpIcon className="w-4 h-4" /> : <ArrowDownIcon className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="flex justify-between pt-4">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Clear All
                </button>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsFilterModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setIsFilterModalOpen(false)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg shadow-sm hover:bg-blue-600 transition-colors"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Bulk University Change Modal */}
      {isUniversityChangeModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full"
          >
            <div className="p-6 bg-purple-500 rounded-t-xl">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <FaExchangeAlt className="w-5 h-5" />
                Bulk University Assignment
              </h3>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-700 mb-2">
                  You are about to reassign <span className="font-semibold">{bulkSelectedRestaurants.length} restaurant(s)</span> to a new university.
                </p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-yellow-800 text-sm flex items-center gap-2">
                    <ExclamationTriangleIcon className="w-4 h-4" />
                    <strong>Silent Operation:</strong> Restaurants will NOT be notified of this change.
                  </p>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target University
                </label>
                <select
                  value={bulkTargetUniversity}
                  onChange={(e) => setBulkTargetUniversity(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={isBulkUpdateLoading}
                >
                  <option value="">Select target university</option>
                  {universities.map(university => (
                    <option key={university._id} value={university._id}>
                      {university.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="mb-4 max-h-40 overflow-y-auto">
                <p className="text-sm font-medium text-gray-700 mb-2">Selected Restaurants:</p>
                <div className="space-y-1">
                  {bulkSelectedRestaurants.map(restaurantId => {
                    const restaurant = restaurants.find(r => r._id === restaurantId);
                    return restaurant ? (
                      <div key={restaurantId} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <span className="font-medium">{restaurant.name}</span>
                        <span className="text-gray-500">
                          {universities.find(u => u._id === restaurant.university)?.name || "No university"}
                        </span>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setIsUniversityChangeModalOpen(false);
                    setBulkSelectedRestaurants([]);
                    setBulkTargetUniversity("");
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isBulkUpdateLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUniversityChange}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg shadow-sm hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={!bulkTargetUniversity || isBulkUpdateLoading}
                >
                  {isBulkUpdateLoading ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FaExchangeAlt className="w-4 h-4" />
                      Reassign {bulkSelectedRestaurants.length} Restaurant(s)
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Restaurants;
